import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronRight, Building2, Crown, UserPlus, Pencil, Trash2, KeyRound, Search, ShieldCheck, UserX, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { ChampionBadges } from "@/components/Badges";
import { roleLabel, roleBadgeClass } from "@/lib/helpers";

function UserRow({ u, depth, a }) {
  return (
    <div className="group flex items-center gap-3 py-2 pr-3 hover:bg-slate-800/30 rounded-lg transition-colors" style={{ paddingLeft: depth * 28 + 8 }}
         data-testid={`user-row-${u.username}`}>
      <span className="h-px w-4 bg-slate-700 shrink-0" />
      <Checkbox checked={a.selected.has(u.id)} onCheckedChange={() => a.toggleSelect(u.id)} data-testid={`user-select-${u.username}`} />
      <UserAvatar path={u.photo_path} name={u.full_name} className="h-9 w-9" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/profile/${u.id}`} className={`font-semibold hover:text-yellow-200 ${u.is_active ? "" : "line-through text-slate-500"}`} data-testid={`user-profile-link-${u.username}`}>{u.full_name}</Link>
          <Badge className={roleBadgeClass(u.custom_role_id ? "custom" : u.role)} data-testid={`user-role-${u.username}`}>{roleLabel(u)}</Badge>
          <ChampionBadges userId={u.id} badges={a.badges} />
        </div>
        <div className="font-mono text-[11px] text-slate-500">{u.username}
          {a.pins && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200 tracking-widest" data-testid={`user-pin-${u.username}`}>PIN {a.pins[u.id] || "—"}</span>}
        </div>
      </div>
      <Switch checked={u.is_active} onCheckedChange={() => a.toggleActive(u)} data-testid={`user-active-${u.username}`} />
      <div className="flex whitespace-nowrap">
        <Button size="sm" variant="ghost" onClick={() => a.openEdit(u)} data-testid={`user-edit-${u.username}`}><Pencil size={14} /></Button>
        {a.me?.role === "koordinator" && u.id !== a.me.id && (
          <Button size="sm" variant="ghost" onClick={() => a.resetPw(u)} title="Şifre sıfırla" data-testid={`user-reset-pw-${u.username}`}><KeyRound size={14} className="text-amber-300" /></Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => a.remove(u)} data-testid={`user-delete-${u.username}`}><Trash2 size={14} className="text-red-400" /></Button>
      </div>
    </div>
  );
}

function Level({ label, icon: Icon, users, depth, a, tone }) {
  if (!users.length) return null;
  return (
    <div>
      <div className="flex items-center gap-2 py-1 text-[10px] font-mono uppercase tracking-widest text-slate-500" style={{ paddingLeft: depth * 28 + 8 }}>
        <Icon size={12} className={tone} /> {label} · {users.length}
      </div>
      {users.map((u) => <UserRow key={u.id} u={u} depth={depth + 1} a={a} />)}
    </div>
  );
}

function Node({ id, title, sub, icon: Icon, color, count, open, onToggle, onAdd, children }) {
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass overflow-hidden" data-testid={`tree-node-${id}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={onToggle} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`tree-toggle-${id}`}>
          <ChevronRight size={16} className={`text-slate-500 transition-transform ${open ? "rotate-90" : ""}`} />
          <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}22`, border: `1px solid ${color}55` }}><Icon size={18} style={{ color }} /></span>
          <div className="min-w-0"><div className="font-display font-bold truncate">{title}</div><div className="text-[11px] text-slate-500 truncate">{sub}</div></div>
        </button>
        <span className="text-xs text-slate-400 tabular-nums" data-testid={`tree-count-${id}`}>{count} kişi</span>
        {onAdd && <Button size="sm" onClick={onAdd} className="bg-blue-600/20 text-blue-200 hover:bg-blue-600/30 border border-blue-500/30" data-testid={`tree-add-${id}`}><UserPlus size={14} className="mr-1" /> Ekle</Button>}
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                      className="border-t border-slate-800 py-2 px-2">{children}</motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

const isMgmt = (u) => (u.role === "koordinator" || u.role === "patron") && !u.branch_id;

export function UserTree({ items, branches, a, onAdd }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({ mgmt: true });
  const filtered = useMemo(() => {
    const s = q.trim().toLocaleLowerCase("tr");
    return s ? items.filter((u) => `${u.full_name} ${u.username}`.toLocaleLowerCase("tr").includes(s)) : items;
  }, [items, q]);
  const isOpen = (id) => !!q || !!open[id];
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const all = (v) => setOpen(Object.fromEntries(["mgmt", "none", ...branches.map((b) => b.id)].map((k) => [k, v])));
  const mgmt = filtered.filter(isMgmt);
  const noBranch = filtered.filter((u) => !isMgmt(u) && !branches.some((b) => b.id === u.branch_id));
  return (
    <div className="space-y-3" data-testid="user-tree">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kullanıcı ara..." className="pl-9 bg-slate-900/60 border-slate-700" data-testid="user-tree-search" />
        </div>
        <Button variant="outline" size="sm" className="border-slate-700" onClick={() => all(true)} data-testid="tree-expand-all"><ChevronsUpDown size={14} className="mr-1" /> Tümünü Aç</Button>
        <Button variant="outline" size="sm" className="border-slate-700" onClick={() => all(false)} data-testid="tree-collapse-all"><ChevronsDownUp size={14} className="mr-1" /> Kapat</Button>
      </div>
      <Node id="mgmt" title="Karahan Teknoloji · Yönetim" sub="Patron ve Koordinatörler" icon={Crown} color="#f59e0b" count={mgmt.length}
            open={isOpen("mgmt")} onToggle={() => toggle("mgmt")} onAdd={() => onAdd({ role: "koordinator", branch_id: "" })}>
        <Level label="Patron" icon={Crown} tone="text-amber-300" users={mgmt.filter((u) => u.role === "patron" && !u.custom_role_id)} depth={0} a={a} />
        <Level label="Koordinatör" icon={ShieldCheck} tone="text-blue-300" users={mgmt.filter((u) => u.role === "koordinator")} depth={0} a={a} />
        <Level label="Özel Roller" icon={ShieldCheck} tone="text-fuchsia-300" users={mgmt.filter((u) => u.role === "patron" && u.custom_role_id)} depth={0} a={a} />
      </Node>
      <div className="pl-4 border-l-2 border-slate-800 ml-6 space-y-3">
        {branches.map((b) => {
          const us = filtered.filter((u) => !isMgmt(u) && u.branch_id === b.id);
          if (q && !us.length) return null;
          return (
            <Node key={b.id} id={b.id} title={b.name} sub={`Şube kodu ${b.code || "-"}`} icon={Building2} color="#3b82f6" count={us.length}
                  open={isOpen(b.id)} onToggle={() => toggle(b.id)} onAdd={() => onAdd({ role: "personel", branch_id: b.id })}>
              <Level label="Şube Müdürü" icon={Crown} tone="text-emerald-300" users={us.filter((u) => u.role === "mudur")} depth={0} a={a} />
              <Level label="Personel" icon={Building2} tone="text-slate-300" users={us.filter((u) => u.role !== "mudur")} depth={1} a={a} />
              {!us.length && <div className="text-center text-xs text-slate-500 py-4">Bu şubede kullanıcı yok — “Ekle” ile tanımlayın</div>}
            </Node>
          );
        })}
        {noBranch.length > 0 && (
          <Node id="none" title="Şubesi atanmamış" sub="Bir şubeye bağlayın" icon={UserX} color="#94a3b8" count={noBranch.length} open={isOpen("none")} onToggle={() => toggle("none")}>
            {noBranch.map((u) => <UserRow key={u.id} u={u} depth={0} a={a} />)}
          </Node>
        )}
      </div>
    </div>
  );
}
